import { useEffect, useRef } from "react";
import { toast } from "react-toastify";

import { formatMoney, parseFormattedMoney } from "@client/utils/formatMoney";
import { roundValue } from "@lib/roundValue";
import clsx from "clsx";

function getFormattedMoney(
  value: number | undefined,
  isMoney: boolean | undefined,
  hideIfZero: boolean,
): string {
  if (!isMoney) {
    return value === undefined ? "" : "" + value;
  }

  if ((value ?? 0) !== 0 || !hideIfZero) {
    return formatMoney(value ?? 0);
  }

  return "";
}

// NOTE: this uses an unbound input element
export function EditableNumberField({
  value,
  isMoney,
  onSubmitted,
  disabled,
  min,
  max,
  onFocus,
  hideIfZero = true,
  align = "text-right",
  className,
  hasBorder = false,
}: {
  value: number | undefined;
  isMoney?: boolean;
  onSubmitted: (value: number) => void;
  disabled?: boolean;
  min: number;
  max: number;
  onFocus?: () => void;
  hideIfZero?: boolean;
  align?: string;
  className?: string;
  hasBorder?: boolean;
}) {
  const currentValueRef = useRef<number | undefined>(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const focusRef = useRef<boolean>(false);
  const lastInputValueRef = useRef<string>(value + "");
  const prettyFormattedValue = getFormattedMoney(
    currentValueRef.current,
    isMoney,
    hideIfZero,
  );

  function setInputValue(newInputValue: string, select: boolean = false) {
    lastInputValueRef.current = newInputValue;
    if (inputRef.current) {
      inputRef.current.value = newInputValue;
      inputRef.current.placeholder = newInputValue;

      if (select) {
        inputRef.current.select();
      }
    }
  }

  useEffect(() => {
    // if the value prop changes while the input is focussed, then we don't want
    // to change anything until the user submits. in the handleSubmit function
    // if currentValueRef.current === newValue (the input value is unchanged),
    // then currentValueRef.current = value (use the new value provided by
    // react)
    if (focusRef.current) {
      return;
    }

    currentValueRef.current = value ?? 0;
    setInputValue(getFormattedMoney(value, isMoney, hideIfZero));
  }, [value, isMoney, hideIfZero]);

  function handleFocus() {
    focusRef.current = true;
    if (inputRef.current) {
      setInputValue((currentValueRef.current ?? 0) + "", true);
    }

    onFocus?.();
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    lastInputValueRef.current = event.target.value;
  };

  const handleBlur = () => {
    focusRef.current = false;
    handleSubmit();
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      // This will cause a blur event which submits with `handleBlur`
      inputRef.current?.blur();
    }

    if (event.keyCode === 90 && (event.ctrlKey || event.metaKey)) {
      // Ctrl+Z or Cmd+Z. Stop propagation so that this doesn't get handled by the round-level keyboard handler, which would undo the last saved change.
      event.stopPropagation();
    }
  };

  function parseBeforeSubmit(inputValue: string | null) {
    if (!inputValue) return 0;
    if (isMoney) {
      return parseFormattedMoney(inputValue);
    } else {
      return roundValue(parseFloat(inputValue ?? ""));
    }
  }

  function handleSubmit() {
    if (disabled) return;

    let newValue = parseBeforeSubmit(lastInputValueRef.current);

    if (isNaN(newValue)) {
      toast.error(`Failed to parse value: ${lastInputValueRef.current}`);
      setInputValue(prettyFormattedValue);
      return;
    }

    if (newValue < min) {
      toast.error(
        min === 0
          ? "This value can't be negative"
          : `This value can't be less than ${min}`,
      );
      setInputValue(prettyFormattedValue);
      return;
    }

    if (newValue > max) {
      toast.error(`This value can't be more than ${max}`);
      setInputValue(prettyFormattedValue);
      return;
    }

    if (currentValueRef.current === newValue) {
      if (currentValueRef.current !== value) {
        currentValueRef.current = value;
      }

      // prettyFormattedValue is always using the most recent `value` so we
      // don't need to recalculate it
      setInputValue(prettyFormattedValue);
      return;
    }

    onSubmitted(newValue);

    const newPrettyFormattedValue = getFormattedMoney(
      newValue,
      isMoney,
      hideIfZero,
    );
    setInputValue(newPrettyFormattedValue);
    currentValueRef.current = newValue;
  }

  return (
    <span>
      <input
        onBlur={handleBlur}
        onKeyDown={handleKeyPress}
        onChange={handleChange}
        onFocus={handleFocus}
        ref={inputRef}
        type="text"
        spellCheck={false}
        disabled={disabled}
        className={clsx(
          align,
          "bg-transparent p-0 m-0 outline-none min-w-0",
          hasBorder
            ? "border border-[#444] rounded"
            : "border-none focus:border-none",
          disabled ? "" : "cursor-pointer",
          className,
        )}
        placeholder={prettyFormattedValue}
      />
    </span>
  );
}
